import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FunctionsIcon from '@mui/icons-material/Functions';
import { Box, Tab, Tabs, useTheme } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useAuth } from '@verantos-dev/core-auth';
import {
  AV_CARD_RECALL_FLAG,
  CodesTab,
  ConceptsTab,
  DecisionsTab,
  DefinitionTab,
  DescriptionTab,
  RecallTab,
  ResultsTab,
  SqlTab,
  UsageTab,
  VerificationTab,
  resolveActiveTab,
  tabLabel,
  tabsForArtifact,
  type EpiArtifact,
  type EpiArtifactTab,
} from '@verantos-dev/feature-epi-chat';
import {
  ArtifactStatusChip,
  Icon,
  ProjectDetailHeader,
  type PageHeaderBreadcrumb,
  type ProjectDetailHeaderMetaItem,
} from '@verantos-dev/shared-components';
import { fontSize } from '@verantos-dev/shared-ui-components/tokens';

import { AvDenovoPreviewModal } from './AvDenovoPreviewModal';
import { AvScopeControl, currentScope } from './AvScopeControl';
import { sqlAvDetailToEpiArtifact } from '../../adapters/sqlAvDetailToEpiArtifact';
import {
  resolveVariableStatusChip,
  type VariableDetail,
  type VariableStatusChip,
} from '../../api/useVariableDetail';
import { VariableNameInlineEdit } from '../../components/VariableNameInlineEdit';

const HEADER_ICON_SX = { fontSize: fontSize[16], color: 'text.secondary' };

const DEFAULT_TAB_SEARCH_PARAM = 'tab';

const ALL_TABS: ReadonlySet<EpiArtifactTab> = new Set<EpiArtifactTab>([
  'description',
  'sql',
  'verification',
  'results',
  'definition',
  'codes',
  'concepts',
  'decisions',
  'recall',
  'usage',
]);

const isEpiArtifactTab = (value: string | null | undefined): value is EpiArtifactTab =>
  typeof value === 'string' && ALL_TABS.has(value as EpiArtifactTab);

const formatLastModified = (iso: string | null | undefined): string | undefined => {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const renderTabBody = (
  artifact: EpiArtifact,
  tab: EpiArtifactTab,
  onJumpToSqlLine: (line: number) => void,
  onAvNameClick?: (id: string, options?: { includeDeleted?: boolean }) => void,
  handleCloseDialog?: () => void
) => {
  switch (tab) {
    case 'description':
      return <DescriptionTab artifact={artifact} handleCloseDialog={handleCloseDialog} />;
    case 'sql':
      return <SqlTab artifact={artifact} />;
    case 'verification':
      return (
        <VerificationTab
          artifact={artifact}
          onJumpToSqlLine={onJumpToSqlLine}
          onAvNameClick={onAvNameClick}
        />
      );
    case 'results':
      return <ResultsTab artifact={artifact} />;
    case 'definition':
      return <DefinitionTab artifact={artifact} />;
    case 'codes':
      return <CodesTab artifact={artifact} />;
    case 'concepts':
      return <ConceptsTab artifact={artifact} />;
    case 'decisions':
      return <DecisionsTab artifact={artifact} />;
    case 'recall':
      return <RecallTab artifact={artifact} />;
    case 'usage':
      return <UsageTab artifact={artifact} />;
  }
};

export interface AvContentCardProps {
  liveArtifact?: EpiArtifact | null;
  detail?: VariableDetail | null;
  statusChip?: VariableStatusChip;
  isStreaming?: boolean;
  headerFallbackName?: string;
  visibleTabs?: readonly EpiArtifactTab[];
  initialTab?: EpiArtifactTab;
  /**
   * URL query-parameter key for the active tab. Defaults to ``tab``.
   * Persisting through the URL means the user's selection survives a
   * remount of the surface (e.g. the project page's outlet getting
   * shuffled when the chat panel collapses) without a higher-level
   * state container.
   */
  tabSearchParam?: string;
  onJumpToSqlLine?: (line: number) => void;
  /** Closes the host dialog on a "Used in" row-link click; passed by modal hosts only. */
  handleCloseDialog?: () => void;
  footer?: React.ReactNode;
  /**
   * Breadcrumb trail rendered above the title (route mount). When supplied,
   * the breadcrumb + title share one condensed header — tight spacing,
   * consistent with the other project detail pages. Modal hosts omit it and
   * get a title-only header.
   */
  breadcrumbs?: PageHeaderBreadcrumb[];
  'data-testid'?: string;
}

/**
 * Portable AV-content surface. Owns the header (title row with status
 * chips + meta row with category / owner / last-modified), the tab
 * strip, and the tab body. Designed to drop into three call sites:
 *
 *   - Project AV detail page (full tabset, page chrome).
 *   - Cohort-criterion popup modal (trimmed to ``['description', 'sql']``;
 *     "View full" footer linking to the standalone page).
 *   - Chat artifact drawer (future; lives next to the chat stream's
 *     ``EpiArtifact`` rather than a REST detail).
 *
 * Header visual matches the analytical-variable edit page so the two
 * surfaces feel like the same artifact viewed in different chrome —
 * single bottom border on the tab strip, not stacked under the header.
 */
export const AvContentCard = ({
  liveArtifact,
  detail,
  statusChip: statusChipProp,
  isStreaming = false,
  headerFallbackName,
  visibleTabs,
  initialTab,
  tabSearchParam = DEFAULT_TAB_SEARCH_PARAM,
  onJumpToSqlLine,
  handleCloseDialog,
  footer,
  breadcrumbs,
  'data-testid': dataTestId = 'av-content-card',
}: AvContentCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [openedAvName, setOpenedAvName] = useState<string | null>(null);
  const [openedAvIncludeDeleted, setOpenedAvIncludeDeleted] = useState(false);

  const statusChipLabel: Record<VariableStatusChip, string> = {
    working: formatMessage({ id: 'projects.analyticalVariables.contentCard.statusChip.working' }),
    drafting: formatMessage({ id: 'projects.analyticalVariables.contentCard.statusChip.drafting' }),
    verified: formatMessage({ id: 'projects.analyticalVariables.contentCard.statusChip.verified' }),
    error: formatMessage({ id: 'projects.analyticalVariables.contentCard.statusChip.error' }),
    saved: formatMessage({ id: 'projects.analyticalVariables.contentCard.statusChip.saved' }),
  };

  // Live wins; REST synthesizes when nothing live is in scope.
  const artifact: EpiArtifact | null = useMemo(() => {
    if (liveArtifact) return liveArtifact;
    if (detail) return sqlAvDetailToEpiArtifact(detail);
    return null;
  }, [liveArtifact, detail]);

  // Manifest-enriched tabs (Concepts / Decisions / Recall) are
  // split-flag gated — the helpers stay pure and we feed the boolean
  // in from the renderer (same pattern as ArtifactDrawer). When
  // ``visibleTabs`` is supplied (the cohort criterion popup trims to
  // description+sql), the flag is still evaluated but the subsequent
  // filter drops the enriched tabs anyway. Coerce to bool — Split
  // treatments can be string-valued; we only care about truthy/falsy
  // for the tab gate.
  const avCardRecallEnabled = Boolean(useFeatureFlagSplit(AV_CARD_RECALL_FLAG));

  const {
    user: { userId: currentUserId },
  } = useAuth();
  const tabOptions = useMemo(() => ({ avCardRecallEnabled }), [avCardRecallEnabled]);
  const tabs = useMemo<EpiArtifactTab[]>(() => {
    const fromArtifact = tabsForArtifact(artifact, tabOptions);
    if (!visibleTabs) return fromArtifact;
    const allowed = new Set(visibleTabs);
    return fromArtifact.filter((tab) => allowed.has(tab));
  }, [artifact, tabOptions, visibleTabs]);

  // Active tab persists in the URL so the choice survives a remount
  // (e.g. the project page's outlet getting reshuffled when the chat
  // panel collapses). Multiple cards on the same route can scope
  // independently via the ``tabSearchParam`` prop.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTabRaw = searchParams.get(tabSearchParam);
  const urlTab: EpiArtifactTab | null = isEpiArtifactTab(urlTabRaw) ? urlTabRaw : null;

  const resolvedTab = useMemo<EpiArtifactTab>(
    () => resolveActiveTab(artifact, urlTab ?? initialTab ?? null, tabOptions) as EpiArtifactTab,
    [artifact, urlTab, initialTab, tabOptions]
  );
  const visibleTab: EpiArtifactTab = tabs.includes(resolvedTab)
    ? resolvedTab
    : tabs[0] ?? 'description';

  const writeTabToUrl = useCallback(
    (next: EpiArtifactTab) => {
      // ``replace`` so the back button doesn't pile up an entry per
      // tab click. Preserve other query params (``chat=`` survives).
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set(tabSearchParam, next);
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams, tabSearchParam]
  );

  const handleJumpToSqlLine = useCallback(
    (line: number) => {
      onJumpToSqlLine?.(line);
      writeTabToUrl('sql');
    },
    [onJumpToSqlLine, writeTabToUrl]
  );

  const handleAvNameClick = useCallback((id: string, options?: { includeDeleted?: boolean }) => {
    // OR-6234: a verification result's anchor row asks for a preview that resolves
    // a deleted variable; every other opener does not, so the flag travels per click.
    setOpenedAvIncludeDeleted(options?.includeDeleted === true);
    setOpenedAvName(id);
  }, []);

  if (!artifact) {
    return null;
  }

  const chip = statusChipProp ?? resolveVariableStatusChip(detail, { isStreaming });
  const scope = detail ? currentScope(detail) : null;
  const headerTitle =
    detail?.dataElement || artifact.dataElement || headerFallbackName || 'Variable';

  // Inline rename is available for a saved library row or an unsaved
  // draft, gated on the creator.
  const creatorUserId = detail?.createdBy?.userId ?? null;
  const isNameEditable =
    (detail?.source === 'library' || detail?.source === 'knowledge') &&
    creatorUserId !== null &&
    creatorUserId === currentUserId;

  // Meta row sources. Library + knowledge details both surface ``owner``
  // and ``modifiedBy`` / ``createdBy``; the live chat artifact carries
  // ``updatedAt`` instead — fall back to it when no REST detail is in
  // scope.
  const owner = detail?.owner ?? null;
  const lastModified =
    detail?.modifiedBy?.transactionDate ??
    detail?.createdBy?.transactionDate ??
    artifact.updatedAt ??
    null;
  const lastModifiedLabel = formatLastModified(lastModified);

  // Condensed header meta row (Type • Owner • Last modified), rendered by the
  // shared ProjectDetailHeader so the AV header matches the other project
  // detail pages. No breadcrumb here — the page renders its own above this
  // card, and the modal hosts have none.
  const metaItems = (
    [
      {
        key: 'type',
        icon: <FunctionsIcon sx={HEADER_ICON_SX} />,
        label: 'Type:',
        value: 'Variable',
      },
      scope
        ? {
            key: 'scope',
            icon: <AccountTreeOutlinedIcon sx={HEADER_ICON_SX} />,
            label: 'Scope:',
            value: formatMessage({
              id: `projects.analyticalVariables.contentCard.meta.scope.${scope}`,
            }),
          }
        : null,
      owner
        ? {
            key: 'owner',
            icon: (
              <Icon
                id={`${dataTestId}-owner-icon`}
                icon="owner"
                width={16}
                height={16}
                sx={{ fill: (t) => t.palette.text.secondary }}
              />
            ),
            label: 'Owner:',
            value: owner,
          }
        : null,
      lastModifiedLabel
        ? {
            key: 'lastModified',
            icon: <AccessTimeOutlinedIcon sx={HEADER_ICON_SX} />,
            label: 'Last modified:',
            value: lastModifiedLabel,
          }
        : null,
    ] as (ProjectDetailHeaderMetaItem | null)[]
  ).filter((item): item is ProjectDetailHeaderMetaItem => item !== null);

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <ProjectDetailHeader
        id={dataTestId}
        breadcrumbs={breadcrumbs}
        title={headerTitle}
        titleSlot={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <VariableNameInlineEdit
              analyticalVariableId={detail?.analyticalVariableId ?? ''}
              name={headerTitle}
              isEditable={isNameEditable}
              dataTestId={dataTestId}
              projectId={detail?.projectId ?? undefined}
            />
            {detail?.assetStatus ? (
              <ArtifactStatusChip
                status={detail.assetStatus}
                data-testid={`${dataTestId}-status`}
              />
            ) : (
              <Box
                component="span"
                sx={{
                  paddingX: 1,
                  paddingY: 0.25,
                  borderRadius: 1,
                  backgroundColor:
                    chip === 'working' ? theme.palette.info.light : theme.palette.action.hover,
                  color:
                    chip === 'working'
                      ? theme.palette.info.contrastText
                      : theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  flexShrink: 0,
                }}
                data-testid={`${dataTestId}-status`}
              >
                {statusChipLabel[chip]}
              </Box>
            )}
          </Box>
        }
        rightContent={
          // Publish / demote control: only a saved library row can change
          // scope. An unsaved draft (``source === 'knowledge'``) has no
          // ``ANALYTICAL_VARIABLE`` row to PATCH, so the control must not
          // render — ``currentScope`` treats a null scope as "project", so
          // gating on ``source`` (not on a truthy ``detail``) is what keeps
          // the publish button off drafts.
          detail?.source === 'library' ? (
            <AvScopeControl detail={detail} data-testid={`${dataTestId}-scope-control`} />
          ) : null
        }
        metaItems={metaItems}
      />

      {tabs.length > 0 && (
        // Gutter wrapper (rather than paddingX on the Tabs) so the bottom
        // divider starts at the 24px content gutter — flush with the header
        // title — instead of spanning the full panel width. Parity with the
        // gen-3 cohort detail tabs.
        <Box sx={{ paddingX: 6 }}>
          <Tabs
            value={visibleTab}
            onChange={(_, value) => writeTabToUrl(value as EpiArtifactTab)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 36, // TODO: no VDS token for this value — needs designer input
              borderBottom: `1px solid ${theme.palette.divider}`,
              '& .MuiTab-root': {
                minHeight: 36, // TODO: no VDS token for this value — needs designer input
                paddingY: 0.5,
                textTransform: 'uppercase',
                fontSize: theme.typography.body2.fontSize,
              },
              // First tab flush-left so its label + indicator align with the
              // content gutter (and the header title above).
              '& .MuiTab-root:first-of-type': {
                paddingLeft: 0,
                minWidth: 0,
              },
            }}
            data-testid={`${dataTestId}-tabs`}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab}
                value={tab}
                label={tabLabel(tab)}
                data-testid={`${dataTestId}-tab-${tab}`}
              />
            ))}
          </Tabs>
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {renderTabBody(
          artifact,
          visibleTab,
          handleJumpToSqlLine,
          handleAvNameClick,
          handleCloseDialog
        )}
      </Box>
      {footer ? <Box sx={{ flexShrink: 0 }}>{footer}</Box> : null}
      {openedAvName && (
        <AvDenovoPreviewModal
          id={`av-name-preview-${openedAvName}`}
          analyticalVariableId={openedAvName}
          isOpen={true}
          includeDeleted={openedAvIncludeDeleted}
          onClose={() => setOpenedAvName(null)}
        />
      )}
    </Box>
  );
};
